// Bitbucket OAuth2 — Authorization Code + PKCE (public client)
//
// Offline-first notes:
//   - Access tokens expire in ~2 hours; refresh tokens are long-lived
//   - Token stored in IndexedDB, survives app restarts
//   - When offline, cached data is served; token refresh is skipped gracefully
//
// OAuth Consumer requirements (user registers their own Bitbucket OAuth Consumer):
//   - client_id (Key)     — from Bitbucket workspace > Settings > OAuth Consumers
//   - client_secret       — from the same consumer (optional with PKCE for public clients)
//   - redirect_uri        — verigit://oauth/callback (Electron) or <origin>/oauth/callback
//
// Endpoints:
//   Auth URL  : https://bitbucket.org/site/oauth2/authorize
//   Token URL : https://bitbucket.org/site/oauth2/access_token  (Basic auth: client_id:secret)
//   User URL  : https://api.bitbucket.org/2.0/user
//
// Default scopes: repository account email pullrequest

import {
  generateRandomString,
  generateCodeChallenge,
  isElectron,
  proxyUrl,
  parseCallbackUrl,
  openElectronOAuth2Popup,
  openBrowserOAuth2Popup,
  getOAuth2RedirectUri,
  EXPIRY_BUFFER_MS,
} from './platform-oauth2';
import type { PlatformOAuth2Token, PlatformOAuthState } from './types';
import {
  getPlatformOAuth2Token,
  savePlatformOAuth2Token,
  clearPlatformOAuth2Token,
} from './storage';

const BITBUCKET_AUTH_URL = 'https://bitbucket.org/site/oauth2/authorize';
const BITBUCKET_TOKEN_URL = 'https://bitbucket.org/site/oauth2/access_token';
const BITBUCKET_USER_URL = 'https://api.bitbucket.org/2.0/user';

export const BITBUCKET_OAUTH_SCOPES = 'repository account email pullrequest';

const PLATFORM = 'bitbucket' as const;

// ---------------------------------------------------------------------------
// BitbucketOAuth2Service
// ---------------------------------------------------------------------------

class BitbucketOAuth2Service {
  private static instance: BitbucketOAuth2Service;
  private clientId = '';
  private clientSecret = '';
  private redirectUri = '';
  private refreshInProgress = false;
  private stateCallbacks: Set<(state: PlatformOAuthState) => void> = new Set();

  private constructor() {}

  static getInstance(): BitbucketOAuth2Service {
    if (!BitbucketOAuth2Service.instance) {
      BitbucketOAuth2Service.instance = new BitbucketOAuth2Service();
    }
    return BitbucketOAuth2Service.instance;
  }

  configure(clientId: string, clientSecret: string, redirectUri?: string): void {
    this.clientId = clientId;
    this.clientSecret = clientSecret;
    this.redirectUri = redirectUri ?? getOAuth2RedirectUri();
    console.log('[BitbucketOAuth2] Configured', { clientId, redirectUri: this.redirectUri });
  }

  onStateChange(cb: (state: PlatformOAuthState) => void): () => void {
    this.stateCallbacks.add(cb);
    return () => this.stateCallbacks.delete(cb);
  }

  private notifyStateChange(state: PlatformOAuthState): void {
    this.stateCallbacks.forEach((cb) => {
      try { cb(state); } catch (e) { console.error('[BitbucketOAuth2] State callback error:', e); }
    });
  }

  private computeState(token: PlatformOAuth2Token | null): PlatformOAuthState {
    if (!token) {
      return {
        isAuthenticated: false,
        isExpired: false,
        canRefresh: false,
        isOfflineMode: !navigator.onLine,
        expiresInMs: 0,
      };
    }
    const now = Date.now();
    const expiresInMs =
      token.expiresAt === 0 ? Number.POSITIVE_INFINITY : token.expiresAt - now - EXPIRY_BUFFER_MS;
    return {
      isAuthenticated: true,
      isExpired: token.expiresAt !== 0 && expiresInMs <= 0,
      canRefresh: !!token.refreshToken,
      isOfflineMode: !navigator.onLine,
      expiresInMs,
      token,
    };
  }

  async getState(): Promise<PlatformOAuthState> {
    const token = await getPlatformOAuth2Token(PLATFORM);
    return this.computeState(token);
  }

  // ---------------------------------------------------------------------------
  // Auth flow
  // ---------------------------------------------------------------------------

  private async buildAuthUrl(): Promise<{ url: string; state: string; verifier: string }> {
    if (!this.clientId) {
      throw new Error(
        'BitbucketOAuth2Service not configured — call configure() first with your Bitbucket OAuth Consumer credentials'
      );
    }
    const stateParam = generateRandomString(32);
    const verifier = generateRandomString(64);
    const { challenge, method } = await generateCodeChallenge(verifier);

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: BITBUCKET_OAUTH_SCOPES,
      state: stateParam,
      code_challenge: challenge,
      code_challenge_method: method,
    });
    return {
      url: `${BITBUCKET_AUTH_URL}?${params.toString()}`,
      state: stateParam,
      verifier,
    };
  }

  private async exchangeCodeForTokens(
    code: string,
    verifier: string
  ): Promise<PlatformOAuth2Token> {
    console.log('[BitbucketOAuth2] Exchanging code for tokens...');

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: this.redirectUri,
      code_verifier: verifier,
    });

    // Bitbucket token endpoint requires HTTP Basic auth with client_id:client_secret
    const basicAuth = btoa(`${this.clientId}:${this.clientSecret}`);
    const fetchUrl = isElectron ? BITBUCKET_TOKEN_URL : proxyUrl(BITBUCKET_TOKEN_URL);

    const response = await fetch(fetchUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${basicAuth}`,
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Bitbucket token exchange failed (${response.status}): ${text}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(`Bitbucket OAuth error: ${data.error_description ?? data.error}`);
    }

    const now = Date.now();
    let userId: string | undefined;
    let displayName: string | undefined;
    let email: string | undefined;
    let username: string | undefined;
    let avatarUrl: string | undefined;

    try {
      const userFetchUrl = isElectron ? BITBUCKET_USER_URL : proxyUrl(BITBUCKET_USER_URL);
      const userResp = await fetch(userFetchUrl, {
        headers: { Authorization: `Bearer ${data.access_token}` },
      });
      if (userResp.ok) {
        const user = await userResp.json();
        userId = user.account_id;
        displayName = user.display_name;
        username = user.username ?? user.nickname;
        avatarUrl = user.links?.avatar?.href;
        // Fetch email separately (requires `email` scope)
        try {
          const emailFetchUrl = isElectron
            ? 'https://api.bitbucket.org/2.0/user/emails'
            : proxyUrl('https://api.bitbucket.org/2.0/user/emails');
          const emailResp = await fetch(emailFetchUrl, {
            headers: { Authorization: `Bearer ${data.access_token}` },
          });
          if (emailResp.ok) {
            const emailData = await emailResp.json();
            const primary = emailData.values?.find((e: { is_primary: boolean; email: string }) => e.is_primary);
            email = primary?.email;
          }
        } catch { /* email is optional */ }
        console.log('[BitbucketOAuth2] User profile:', { userId, username, displayName });
      }
    } catch (e) {
      console.warn('[BitbucketOAuth2] Could not fetch user info:', e);
    }

    const token: PlatformOAuth2Token = {
      id: `${PLATFORM}-oauth2`,
      platform: PLATFORM,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt: data.expires_in ? now + data.expires_in * 1000 : 0,
      scopes: BITBUCKET_OAUTH_SCOPES.split(' '),
      tokenType: data.token_type ?? 'Bearer',
      userId,
      displayName,
      email,
      username,
      avatarUrl,
      issuedAt: now,
      offlineCapable: true,
    };

    await savePlatformOAuth2Token(token);
    this.notifyStateChange(this.computeState(token));
    console.log('[BitbucketOAuth2] Token saved. Expires:', new Date(token.expiresAt).toISOString());
    return token;
  }

  async initiateFlow(): Promise<PlatformOAuth2Token> {
    const { url, state, verifier } = await this.buildAuthUrl();
    let callbackUrl: string;
    if (isElectron) {
      callbackUrl = await openElectronOAuth2Popup(url, this.redirectUri);
    } else {
      callbackUrl = await openBrowserOAuth2Popup(url, 'bitbucket-oauth2-callback');
    }
    const { code } = parseCallbackUrl(callbackUrl, state);
    return this.exchangeCodeForTokens(code, verifier);
  }

  // ---------------------------------------------------------------------------
  // Token refresh
  // ---------------------------------------------------------------------------

  async refreshAccessToken(): Promise<PlatformOAuth2Token | null> {
    if (!navigator.onLine) {
      console.log('[BitbucketOAuth2] Offline — skipping refresh, using cached token');
      return null;
    }
    if (this.refreshInProgress) {
      await new Promise<void>((resolve) => {
        const unsub = this.onStateChange(() => { unsub(); resolve(); });
        setTimeout(() => { unsub(); resolve(); }, 10_000);
      });
      return getPlatformOAuth2Token(PLATFORM);
    }

    const current = await getPlatformOAuth2Token(PLATFORM);
    if (!current?.refreshToken) {
      console.warn('[BitbucketOAuth2] No refresh token available');
      return null;
    }

    this.refreshInProgress = true;
    console.log('[BitbucketOAuth2] Refreshing access token...');

    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: current.refreshToken,
      });
      const basicAuth = btoa(`${this.clientId}:${this.clientSecret}`);
      const fetchUrl = isElectron ? BITBUCKET_TOKEN_URL : proxyUrl(BITBUCKET_TOKEN_URL);

      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Basic ${basicAuth}`,
        },
        body: body.toString(),
      });

      if (!response.ok) {
        console.error(`[BitbucketOAuth2] Refresh failed (${response.status})`);
        return null;
      }

      const data = await response.json();
      const now = Date.now();
      const refreshed: PlatformOAuth2Token = {
        ...current,
        accessToken: data.access_token,
        refreshToken: data.refresh_token ?? current.refreshToken,
        expiresAt: data.expires_in ? now + data.expires_in * 1000 : 0,
        lastRefreshedAt: now,
      };

      await savePlatformOAuth2Token(refreshed);
      this.notifyStateChange(this.computeState(refreshed));
      console.log('[BitbucketOAuth2] Token refreshed. New expiry:', new Date(refreshed.expiresAt).toISOString());
      return refreshed;
    } finally {
      this.refreshInProgress = false;
    }
  }

  async getAccessToken(): Promise<string | null> {
    const token = await getPlatformOAuth2Token(PLATFORM);
    if (!token) return null;

    const state = this.computeState(token);
    if (!state.isExpired) return token.accessToken;
    if (!navigator.onLine) return token.accessToken; // offline — return stale token

    const refreshed = await this.refreshAccessToken();
    return refreshed?.accessToken ?? null;
  }

  async revokeToken(): Promise<void> {
    await clearPlatformOAuth2Token(PLATFORM);
    this.notifyStateChange(this.computeState(null));
    console.log('[BitbucketOAuth2] Token revoked and cleared from storage');
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const bitbucketOAuth2 = BitbucketOAuth2Service.getInstance();
